// client/pages/admin/RateCards.tsx
// T-15 / KX-106 — rate cards: the versioned revenue split (60/30/10) per country.
//
// Lifecycle with DUAL CONTROL: a card is created as a DRAFT, edited while draft,
// SUBMITTED, then APPROVED by a different admin (the backend refuses approval by
// the author/submitter). Approval requires effective_from >= 30 days out.
// Settlement reads the card STAMPED on a subscription, never "the current one".
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import {
  Percent, Loader2, AlertCircle, Check, RefreshCw, Plus,
  Send, CheckCircle2, XCircle, Pencil,
} from "lucide-react";
import {
  getRateCards, createRateCard, updateRateCard,
  submitRateCard, approveRateCard, rejectRateCard,
  RateCard,
} from "../../api/services/rateCardService";

const fld =
  "w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500";

interface Draft {
  country_code: string;
  label: string;
  field_bps: number;
  company_bps: number;
  treasury_bps: number;
  acquisition_bps: number;
  service_pool_bps: number;
  standby_bps: number;
  team_leader_bps: number;
  country_manager_bps: number;
  effective_from: string;
}

// The canonical 60/30/10 policy: field 6000 = acquisition 1000 + service pool 5000.
const EMPTY: Draft = {
  country_code: "",
  label: "",
  field_bps: 6000,
  company_bps: 3000,
  treasury_bps: 1000,
  acquisition_bps: 1000,
  service_pool_bps: 5000,
  standby_bps: 0,
  team_leader_bps: 0,
  country_manager_bps: 0,
  effective_from: "",
};

const statusColor: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  pending_approval: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
  approved: "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300",
  rejected: "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300",
};

export default function AdminRateCards() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<RateCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const cards = await getRateCards();
      setRows(cards);
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || t("admin.rateCards.errors.load", { defaultValue: "Failed to load rate cards" }));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const num = (v: string) => (v === "" ? 0 : Math.max(0, Math.floor(Number(v) || 0)));

  // Validate the two conservation rules the backend enforces, with clear messages.
  const validate = (d: Draft): string => {
    if (d.country_code && !/^[A-Z]{2}$/.test(d.country_code)) {
      return t("admin.rateCards.errors.badCountry", { defaultValue: "Country must be an ISO-2 code (e.g. NG), or blank for a global card." });
    }
    if (d.field_bps + d.company_bps + d.treasury_bps !== 10000) {
      return t("admin.rateCards.errors.topSum", { defaultValue: "Field + company + treasury must sum to 10000 bps." });
    }
    if ((d.acquisition_bps || d.service_pool_bps) && d.acquisition_bps + d.service_pool_bps !== d.field_bps) {
      return t("admin.rateCards.errors.fieldSum", { defaultValue: "Acquisition + service pool must equal the field share." });
    }
    return "";
  };

  const reset = () => {
    setDraft(EMPTY);
    setEditingId(null);
  };

  const save = async () => {
    const d: Draft = { ...draft, country_code: draft.country_code.toUpperCase().trim() };
    const v = validate(d);
    if (v) {
      setErr(v);
      return;
    }
    setBusy("save");
    setErr("");
    try {
      const payload: Partial<RateCard> = {
        country_code: d.country_code || null,
        label: d.label || null,
        field_bps: d.field_bps,
        company_bps: d.company_bps,
        treasury_bps: d.treasury_bps,
        acquisition_bps: d.acquisition_bps,
        service_pool_bps: d.service_pool_bps,
        standby_bps: d.standby_bps,
        team_leader_bps: d.team_leader_bps,
        country_manager_bps: d.country_manager_bps,
        ...(d.effective_from ? { effective_from: d.effective_from } : {}),
      };
      if (editingId) await updateRateCard(editingId, payload);
      else await createRateCard(payload);
      reset();
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || t("admin.rateCards.errors.save", { defaultValue: "Failed to save" }));
    } finally {
      setBusy(null);
    }
  };

  const edit = (card: RateCard) => {
    setEditingId(card._id || null);
    setDraft({
      country_code: card.country_code || "",
      label: card.label || "",
      field_bps: card.field_bps ?? 0,
      company_bps: card.company_bps ?? 0,
      treasury_bps: card.treasury_bps ?? 0,
      acquisition_bps: card.acquisition_bps ?? 0,
      service_pool_bps: card.service_pool_bps ?? 0,
      standby_bps: card.standby_bps ?? 0,
      team_leader_bps: card.team_leader_bps ?? 0,
      country_manager_bps: card.country_manager_bps ?? 0,
      effective_from: card.effective_from ? String(card.effective_from).slice(0, 10) : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const act = async (id: string | undefined, fn: () => Promise<unknown>) => {
    if (!id) return;
    setBusy(id);
    setErr("");
    try {
      await fn();
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || t("admin.rateCards.errors.action", { defaultValue: "Action failed" }));
    } finally {
      setBusy(null);
    }
  };

  const bpsInput = (key: keyof Draft, labelKey: string, fallback: string) => (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{t(labelKey, { defaultValue: fallback })}</label>
      <input
        type="number"
        min={0}
        max={10000}
        className={fld}
        value={(draft[key] as number) ?? 0}
        onChange={(e) => setDraft({ ...draft, [key]: num(e.target.value) })}
      />
    </div>
  );

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Percent className="w-6 h-6 text-blue-600" />
          <h1 className="text-xl sm:text-2xl font-bold">{t("admin.rateCards.title", { defaultValue: "Rate Cards" })}</h1>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <RefreshCw className="w-4 h-4" /> {t("admin.rateCards.refresh", { defaultValue: "Refresh" })}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        {t("admin.rateCards.description", {
          defaultValue:
            "The versioned revenue split per country, in basis points. A card is created as a draft, submitted, then approved by a different admin (dual control); it takes effect at its effective date (≥ 30 days out).",
        })}
      </p>

      {err && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      <Card className="p-4 mb-6">
        <h2 className="text-sm font-semibold mb-3">
          {editingId
            ? t("admin.rateCards.editDraft", { defaultValue: "Edit draft" })
            : t("admin.rateCards.newDraft", { defaultValue: "New draft" })}
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t("admin.rateCards.countryLabel", { defaultValue: "Country (ISO-2, blank = global)" })}</label>
            <input
              className={fld}
              placeholder="NG"
              maxLength={2}
              value={draft.country_code}
              onChange={(e) => setDraft({ ...draft, country_code: e.target.value.toUpperCase() })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t("admin.rateCards.labelLabel", { defaultValue: "Label" })}</label>
            <input
              className={fld}
              placeholder={t("admin.rateCards.labelPlaceholder", { defaultValue: "e.g. Nigeria 60/30/10" })}
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3 mb-3">
          {bpsInput("field_bps", "admin.rateCards.fieldBps", "Field (bps)")}
          {bpsInput("company_bps", "admin.rateCards.companyBps", "Company (bps)")}
          {bpsInput("treasury_bps", "admin.rateCards.treasuryBps", "Treasury (bps)")}
        </div>
        <div className="grid gap-3 sm:grid-cols-3 mb-3">
          {bpsInput("acquisition_bps", "admin.rateCards.acquisitionBps", "Acquisition (bps)")}
          {bpsInput("service_pool_bps", "admin.rateCards.servicePoolBps", "Service pool (bps)")}
          {bpsInput("team_leader_bps", "admin.rateCards.teamLeaderBps", "Team Leader (bps)")}
        </div>
        <div className="grid gap-3 sm:grid-cols-3 mb-4">
          {bpsInput("standby_bps", "admin.rateCards.standbyBps", "Standby (bps)")}
          {bpsInput("country_manager_bps", "admin.rateCards.countryManagerBps", "Country Manager (bps)")}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t("admin.rateCards.effectiveFrom", { defaultValue: "Effective from" })}</label>
            <input
              type="date"
              className={fld}
              value={draft.effective_from}
              onChange={(e) => setDraft({ ...draft, effective_from: e.target.value })}
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={save}
              disabled={busy === "save"}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {editingId
                ? t("admin.rateCards.saveChanges", { defaultValue: "Save changes" })
                : t("admin.rateCards.createDraft", { defaultValue: "Create draft" })}
            </button>
            {editingId && (
              <button onClick={reset} className="rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm">
                {t("admin.rateCards.cancel", { defaultValue: "Cancel" })}
              </button>
            )}
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-gray-500">{t("admin.rateCards.empty", { defaultValue: "No rate cards yet." })}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-700">
                <th className="py-2 pr-4">{t("admin.rateCards.table.country", { defaultValue: "Country" })}</th>
                <th className="py-2 pr-4">{t("admin.rateCards.table.split", { defaultValue: "Field / Company / Treasury" })}</th>
                <th className="py-2 pr-4">{t("admin.rateCards.table.field", { defaultValue: "Acq / Pool" })}</th>
                <th className="py-2 pr-4">{t("admin.rateCards.table.status", { defaultValue: "Status" })}</th>
                <th className="py-2 pr-4 text-right">{t("admin.rateCards.table.actions", { defaultValue: "Actions" })}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((card) => (
                <tr key={card._id} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-2 pr-4">
                    <span className="font-mono">{card.country_code || t("admin.rateCards.global", { defaultValue: "Global" })}</span>
                    {card.label ? <div className="text-xs text-gray-500">{card.label}</div> : null}
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs">
                    {card.field_bps} / {card.company_bps} / {card.treasury_bps}
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs">
                    {card.acquisition_bps} / {card.service_pool_bps}
                  </td>
                  <td className="py-2 pr-4">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusColor[card.status || "draft"]}`}>
                      {t(`admin.rateCards.status.${card.status || "draft"}`, { defaultValue: card.status || "draft" })}
                    </span>
                  </td>
                  <td className="py-2 pr-4">
                    <div className="flex items-center justify-end gap-2">
                      {card.status === "draft" && (
                        <button
                          onClick={() => edit(card)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <Pencil className="w-3.5 h-3.5" /> {t("admin.rateCards.edit", { defaultValue: "Edit" })}
                        </button>
                      )}
                      {card.status === "draft" && (
                        <button
                          onClick={() => act(card._id, () => submitRateCard(card._id!))}
                          disabled={busy === card._id}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                        >
                          <Send className="w-3.5 h-3.5" /> {t("admin.rateCards.submit", { defaultValue: "Submit" })}
                        </button>
                      )}
                      {card.status === "pending_approval" && (
                        <>
                          <button
                            onClick={() => act(card._id, () => approveRateCard(card._id!))}
                            disabled={busy === card._id}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" /> {t("admin.rateCards.approve", { defaultValue: "Approve" })}
                          </button>
                          <button
                            onClick={() => {
                              const reason = window.prompt(t("admin.rateCards.rejectReason", { defaultValue: "Reason for rejection?" }) || "") || undefined;
                              act(card._id, () => rejectRateCard(card._id!, reason));
                            }}
                            disabled={busy === card._id}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                          >
                            <XCircle className="w-3.5 h-3.5" /> {t("admin.rateCards.reject", { defaultValue: "Reject" })}
                          </button>
                        </>
                      )}
                      {busy === card._id && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
